<script>
    import Button from './Button.svelte';
    import Modal from './Modal.svelte';
    import { paddle_purchase } from './utils/paddle';
    import { logEvent } from './utils/logs';
    import { goto } from '$app/navigation';
    import { discount_data } from './stores/globals';
    import { page } from '$app/stores';
    import { browser } from '$app/environment';
    import { createEventDispatcher, onDestroy, onMount } from 'svelte';
    import { patch_single_product_prices } from './utils/patch_localized_prices';
    import { discount_applies } from './utils/discount';
    import { get_buy_path } from './utils/get_product_path';

    let session = $page.data.session;

    /** @type {Product} */
    export let product;
    /** @deprecated kept for backwards compatibility */
    export let course = undefined;

    export let hidden = false;
    export let feature = 'generic';
    // Class of an element to embed the checkout in (see BuyPage.svelte).
    export let frame_target = '';
    export let stopPropagation = false;
    export let invert = false;
    let className = '';
    export { className as class };

    export let btn_class = ""

    export let cta = '';
    /** @type {'default' | 'small'} */
    export let style = 'default';

    export const show = () => {
        handle_modal_show();
    };

    export let loading = false;

    let show_error = false;
    const dispatch = createEventDispatcher();

    let discount_applicable = false;

    /** @type {Product | undefined} */
    let active_product;

    $: active_product = product || course;

    $: discount_applicable = discount_applies(
        $discount_data,
        active_product?.price_gift
    );

    const get_redirect_path = () => {
        switch (active_product?.type) {
            case 'course':
                return `/courses/${active_product.slug}?poll=true`;
            case 'worksheet':
                return `/worksheets/${active_product.slug}?poll=true`;
            case 'bundle':
                return `/bundles/${active_product.slug}?poll=true`;
            default:
                return '';
        }
    };

    $: cta_label =
        cta ||  'Buy as a gift';

    const success_cb = () => {
        document.removeEventListener('paddle_success', success_cb);
        document.removeEventListener('paddle_close', close_cb);
        dispatch('success');
        logEvent('purchase:success', {
            course: active_product?.slug,
            product_slug: active_product?.slug,
            product_type: active_product?.type,
            feature,
            type: active_product?.type
        });
        show_error = false;

        loading = false;

        const redirect_path = get_redirect_path();
        const redirect_to = redirect_path
            ? `${location.origin}${redirect_path}`
            : '';

        goto(
            `/purchase-success?redirectTo=${encodeURIComponent(redirect_to)}`
        );
        
    };

    const close_cb = (event) => {
        document.removeEventListener('paddle_success', success_cb);
        document.removeEventListener('paddle_close', close_cb);
        const data = event.detail;
        console.log('close gift', active_product?.slug, data);
        loading = false;
        dispatch('close');
        logEvent('purchase:close', {
            course: active_product?.slug,
            product_slug: active_product?.slug,
            product_type: active_product?.type,
            feature,
            type: active_product?.type
        });
    };

    const handle_purchase = (e) => {
        if (stopPropagation && e?.stopPropagation) e.stopPropagation();

        if (!active_product?.price_gift?.price_id) {
            show_error = true;
            return;
        }

        loading = true;
        // console.log('handle_purchase', session);
        dispatch('show');
        logEvent('purchase:show', {
            course: active_product?.slug,
            product_slug: active_product?.slug,
            product_type: active_product?.type,
            feature,
            type: active_product?.type
        });

        document.addEventListener('paddle_success', success_cb);
        document.addEventListener('paddle_close', close_cb);

        // The promo code may have landed in this same tick (BuyPage opens
        // right after the root layout's lookup), before the reactive update.
        discount_applicable = discount_applies(
            $discount_data,
            active_product?.price_gift
        );

        paddle_purchase({
            price_id: active_product.price_gift.price_id,
            discount_id: discount_applicable
                ? $discount_data?.discount_id || null
                : null,
            email: session?.user?.email || null,
            feature,
            frame_target: frame_target || null
        });
    };

    const handle_modal_show = () => {
        handle_purchase();
    };

    // An inline checkout (BuyPage) goes away with this component; an overlay
    // can outlive it (a button in a closed modal) and still needs the callbacks.
    onDestroy(() => {
        if (!browser || !frame_target) return;
        document.removeEventListener('paddle_success', success_cb);
        document.removeEventListener('paddle_close', close_cb);
    });

    onMount(() => {
        if (!active_product) return;

        patch_single_product_prices(active_product)
            .then((p) => {
                product = p;
            })
            .catch((error) =>
                console.warn('Failed to update gift prices:', error)
            );
    });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
{#if !hidden && active_product?.price_gift?.price_id}
    <!-- A real link for agents and no-JS visitors; clicks open the overlay. -->
    {@const gift_path = get_buy_path(active_product, 'buy-gift')}

    <div
        on:click={handle_modal_show}
        class="relative {style === 'small'
            ? 'inline-flex items-center gap-2'
            : ''} {className}"
    >
        <slot {cta_label} {style} {invert} {loading} {browser}>
            {#if style === 'small'}
                <Button
                    class="whitespace-nowrap flex items-center gap-2 {btn_class}"
                    route={gift_path}
                    preventDefault
                    loading={!browser || loading}
                    loading_spinner={browser}
                    {invert}
                >
                    <span aria-hidden="true">🎁</span>
                    <span>{cta_label}</span>
                </Button>
            {:else}
                <Button
                    primary
                    outline
                    lg
                    class="h-full {btn_class}"
                    block
                    route={gift_path}
                    preventDefault
                    loading={!browser || loading}
                    loading_spinner={browser}
                    {invert}
                >
                    <span class="text-3xl">🎁</span>
                    <svelte:fragment slot="small">{cta_label}</svelte:fragment>
                </Button>
            {/if}
        </slot>
    </div>
{/if}

{#if show_error}
    <Modal on:close={() => (show_error = false)}>
        <h1>Something went wrong</h1>
        <p>We could not process your purchase automatically</p>
        <p>
            Please contact us via <a href="mailto:support@critikid.com"
                >support@critikid.com</a
            > to restore your purchase
        </p>
    </Modal>
{/if}
